/**
 * 複数プリンター管理コンポーネント
 * レシート・キッチン・領収書プリンターの統合管理
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
} from '@mui/material';
import {
  Print,
  Add,
  Edit,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Receipt,
  Kitchen,
  Description,
  ExpandMore,
  ExpandLess,
  Bluetooth,
  Wifi,
  Usb,
  LanOutlined,
} from '@mui/icons-material';
import api from '../../services/api';
import PrinterSettings from '../Admin/PrinterSettings';

// プリンター用途
const PRINTER_PURPOSES = [
  { id: 'receipt', name: 'レシート', icon: <Receipt /> },
  { id: 'kitchen', name: 'キッチン伝票', icon: <Kitchen /> },
  { id: 'invoice', name: '領収書', icon: <Description /> },
  { id: 'label', name: 'ラベル', icon: <Print /> },
];

// 接続アイコン
const CONNECTION_ICONS = {
  lan: <LanOutlined />,
  wifi: <Wifi />,
  bluetooth: <Bluetooth />,
  usb: <Usb />,
};

const PrinterManagement = () => {
  const { storeId } = useParams();
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [testingPrinter, setTestingPrinter] = useState(null);
  const [expandedPrinter, setExpandedPrinter] = useState(null);
  const [discoveredPrinters, setDiscoveredPrinters] = useState([]);
  const [discovering, setDiscovering] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    purpose: 'receipt',
    model: '',
    connectionType: 'lan',
    ipAddress: '',
    port: 9100,
    macAddress: '',
    isDefault: false,
    isActive: true,
    paperWidth: 80,
    autoCut: true,
    printDensity: 'normal',
    cashDrawerEnabled: false,
    soundEnabled: true,
  });

  // プリンター一覧取得
  const fetchPrinters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/printers`);
      setPrinters(response.data || []);
    } catch (err) {
      console.error('プリンター一覧の取得に失敗:', err);
      // デモデータ
      setPrinters([
        {
          id: 1,
          name: 'レジプリンター（メイン）',
          purpose: 'receipt',
          model: 'Star mC-Print3',
          connectionType: 'lan',
          ipAddress: '192.168.1.100',
          port: 9100,
          isDefault: true,
          isActive: true,
          status: 'online',
          paperWidth: 80,
          lastPrint: '2026-01-09 12:45:00',
        },
        {
          id: 2,
          name: 'キッチンプリンター',
          purpose: 'kitchen',
          model: 'Epson TM-m30',
          connectionType: 'wifi',
          ipAddress: '192.168.1.101',
          port: 9100,
          isDefault: false,
          isActive: true,
          status: 'online',
          paperWidth: 80,
          lastPrint: '2026-01-09 12:40:00',
        },
        {
          id: 3,
          name: '領収書プリンター',
          purpose: 'invoice',
          model: 'Star TSP100',
          connectionType: 'usb',
          isDefault: true,
          isActive: true,
          status: 'online',
          paperWidth: 80,
          lastPrint: '2026-01-09 11:30:00',
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchPrinters();
  }, [fetchPrinters]);

  // プリンター検出
  const discoverPrinters = async () => {
    setDiscovering(true);
    try {
      const response = await api.get(`/api/stores/${storeId}/printers/discover`);
      setDiscoveredPrinters(response.data || []);
    } catch (err) {
      // デモデータ
      setDiscoveredPrinters([
        { ipAddress: '192.168.1.102', model: 'Star mC-Print3', type: 'lan' },
        { ipAddress: '192.168.1.103', model: 'Epson TM-m30', type: 'lan' },
      ]);
    } finally {
      setDiscovering(false);
    }
  };

  // ダイアログを開く
  const handleOpenDialog = (printer = null) => {
    if (printer) {
      setEditingPrinter(printer);
      setFormData({
        name: printer.name || '',
        purpose: printer.purpose || 'receipt',
        model: printer.model || '',
        connectionType: printer.connectionType || 'lan',
        ipAddress: printer.ipAddress || '',
        port: printer.port || 9100,
        macAddress: printer.macAddress || '',
        isDefault: printer.isDefault || false,
        isActive: printer.isActive !== false,
        paperWidth: printer.paperWidth || 80,
        autoCut: printer.autoCut !== false,
        printDensity: printer.printDensity || 'normal',
        cashDrawerEnabled: printer.cashDrawerEnabled || false,
        soundEnabled: printer.soundEnabled !== false,
      });
    } else {
      setEditingPrinter(null);
      setFormData({
        name: '',
        purpose: 'receipt',
        model: '',
        connectionType: 'lan',
        ipAddress: '',
        port: 9100,
        macAddress: '',
        isDefault: false,
        isActive: true,
        paperWidth: 80,
        autoCut: true,
        printDensity: 'normal',
        cashDrawerEnabled: false,
        soundEnabled: true,
      });
    }
    setDialogOpen(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.name.trim()) {
        setError('プリンター名を入力してください');
        return;
      }

      const data = { ...formData, storeId };

      if (editingPrinter) {
        await api.put(`/api/stores/${storeId}/printers/${editingPrinter.id}`, data);
      } else {
        await api.post(`/api/stores/${storeId}/printers`, data);
      }

      setSuccess(true);
      setDialogOpen(false);
      fetchPrinters();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 削除
  const handleDelete = async (printer) => {
    if (!window.confirm(`「${printer.name}」を削除しますか？`)) return;

    try {
      await api.delete(`/api/stores/${storeId}/printers/${printer.id}`);
      fetchPrinters();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('削除に失敗しました');
    }
  };

  // テスト印刷
  const handleTestPrint = async (printer) => {
    try {
      setTestingPrinter(printer.id);
      await api.post(`/api/stores/${storeId}/printers/${printer.id}/test`);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('テスト印刷に失敗しました');
    } finally {
      setTestingPrinter(null);
    }
  };

  // デフォルト設定
  const handleSetDefault = async (printer) => {
    try {
      await api.put(`/api/stores/${storeId}/printers/${printer.id}/set-default`);
      fetchPrinters();
    } catch (err) {
      setError('デフォルト設定に失敗しました');
    }
  };

  // ステータスチップ
  const getStatusChip = (status) => {
    switch (status) {
      case 'online':
        return <Chip size="small" icon={<CheckCircle />} label="オンライン" color="success" />;
      case 'offline':
        return <Chip size="small" icon={<ErrorIcon />} label="オフライン" color="error" />;
      case 'error':
        return <Chip size="small" icon={<Warning />} label="エラー" color="warning" />;
      default:
        return <Chip size="small" label="不明" />;
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Print />
        プリンター管理
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          操作が完了しました
        </Alert>
      )}

      {/* アクションバー */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          プリンター追加
        </Button>
        <Button
          variant="outlined"
          startIcon={discovering ? <CircularProgress size={20} /> : <Refresh />}
          onClick={discoverPrinters}
          disabled={discovering}
        >
          ネットワーク検出
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchPrinters}
        >
          一覧更新
        </Button>
      </Box>

      {/* 検出されたプリンター */}
      {discoveredPrinters.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'info.light' }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              検出されたプリンター
            </Typography>
            <List dense>
              {discoveredPrinters.map((printer, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {CONNECTION_ICONS[printer.type] || <Print />}
                  </ListItemIcon>
                  <ListItemText
                    primary={printer.model}
                    secondary={printer.ipAddress}
                  />
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          model: printer.model,
                          ipAddress: printer.ipAddress,
                          connectionType: printer.type,
                        }));
                        setDialogOpen(true);
                      }}
                    >
                      追加
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* プリンター一覧 */}
      <Grid container spacing={2}>
        {PRINTER_PURPOSES.map((purpose) => {
          const purposePrinters = printers.filter(p => p.purpose === purpose.id);

          return (
            <Grid item xs={12} key={purpose.id}>
              <Card>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                  >
                    {purpose.icon}
                    {purpose.name}プリンター
                    <Chip size="small" label={`${purposePrinters.length}台`} />
                  </Typography>

                  {purposePrinters.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      登録されていません
                    </Typography>
                  ) : (
                    <List>
                      {purposePrinters.map((printer) => (
                        <React.Fragment key={printer.id}>
                          <ListItem
                            sx={{ bgcolor: 'background.paper', borderRadius: 1, mb: 1 }}
                          >
                            <ListItemIcon>
                              {CONNECTION_ICONS[printer.connectionType] || <Print />}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {printer.name}
                                  {printer.isDefault && (
                                    <Chip size="small" label="デフォルト" color="primary" />
                                  )}
                                  {!printer.isActive && (
                                    <Chip size="small" label="無効" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <>
                                  {printer.model} |{' '}
                                  {printer.connectionType === 'lan' || printer.connectionType === 'wifi'
                                    ? `${printer.ipAddress}:${printer.port}`
                                    : printer.connectionType.toUpperCase()}
                                </>
                              }
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getStatusChip(printer.status)}
                              <IconButton
                                size="small"
                                onClick={() => handleTestPrint(printer)}
                                disabled={testingPrinter === printer.id}
                              >
                                {testingPrinter === printer.id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Print />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(printer)}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setExpandedPrinter(
                                  expandedPrinter === printer.id ? null : printer.id
                                )}
                              >
                                {expandedPrinter === printer.id ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </Box>
                          </ListItem>

                          <Collapse in={expandedPrinter === printer.id}>
                            <Box sx={{ pl: 7, pr: 2, pb: 2 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    最終印刷: {printer.lastPrint || '未使用'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    用紙幅: {printer.paperWidth}mm
                                  </Typography>
                                </Grid>
                              </Grid>
                              <Box sx={{ mt: 1 }}>
                                {!printer.isDefault && (
                                  <Button
                                    size="small"
                                    onClick={() => handleSetDefault(printer)}
                                    sx={{ mr: 1 }}
                                  >
                                    デフォルトに設定
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(printer)}
                                >
                                  削除
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* StarWebPRNT LAN直結印刷設定 */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Wifi />
        StarWebPRNT（LAN直結印刷）
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        ブラウザからLAN経由でStar mC-Print3に直接印刷します。インターネット不要・遅延なし。
      </Typography>
      <PrinterSettings />

      {/* プリンター追加/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPrinter ? 'プリンターを編集' : 'プリンターを追加'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="プリンター名"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>用途</InputLabel>
                <Select
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  label="用途"
                >
                  {PRINTER_PURPOSES.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {p.icon}
                        {p.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="モデル名"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>接続方式</InputLabel>
                <Select
                  value={formData.connectionType}
                  onChange={(e) => setFormData({ ...formData, connectionType: e.target.value })}
                  label="接続方式"
                >
                  <MenuItem value="lan">LAN</MenuItem>
                  <MenuItem value="wifi">Wi-Fi</MenuItem>
                  <MenuItem value="bluetooth">Bluetooth</MenuItem>
                  <MenuItem value="usb">USB</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>用紙幅</InputLabel>
                <Select
                  value={formData.paperWidth}
                  onChange={(e) => setFormData({ ...formData, paperWidth: e.target.value })}
                  label="用紙幅"
                >
                  <MenuItem value={58}>58mm</MenuItem>
                  <MenuItem value={80}>80mm</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(formData.connectionType === 'lan' || formData.connectionType === 'wifi') && (
              <>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="IPアドレス"
                    value={formData.ipAddress}
                    onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="ポート"
                    value={formData.port}
                    onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="有効"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                }
                label="デフォルト"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoCut}
                    onChange={(e) => setFormData({ ...formData, autoCut: e.target.checked })}
                  />
                }
                label="自動カット"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.cashDrawerEnabled}
                    onChange={(e) => setFormData({ ...formData, cashDrawerEnabled: e.target.checked })}
                  />
                }
                label="キャッシュドロワー連動"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrinterManagement;
